import { Component, OnInit, OnDestroy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { TipoMaterialService } from '../../../services/tipo-material/tipo-material.service';
import { TipoMaterial } from '../../../domain/tipo-material.domain';

@Component({
  selector: 'app-tipo-material-edit',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    InputNumberModule,
    ToggleSwitchModule,
    ButtonModule
  ],
  templateUrl: './tipo-material-edit.component.html',
  styleUrls: ['./tipo-material-edit.component.scss']
})
export class TipoMaterialEditComponent implements OnInit, OnDestroy {
  tipoMaterialForm!: FormGroup;
  loading = signal<boolean>(false);
  isEditMode = signal<boolean>(false);
  tipoMaterialId: string | null = null;
  tipoMaterial = signal<TipoMaterial | null>(null);

  private destroy$ = new Subject<void>();
  private tipoMaterialService = inject(TipoMaterialService);

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.setupRouteParamListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupRouteParamListener(): void {
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const newId = params.get('id');
        if (newId !== this.tipoMaterialId) {
          this.loadTipoMaterial();
        }
      });
  }

  initForm(): void {
    this.tipoMaterialForm = this.fb.group({
      codigo: ['', [Validators.required, Validators.maxLength(255)]],
      nombre: ['', [Validators.required, Validators.maxLength(255)]],
      factorPrecio: [0, [Validators.required, Validators.min(0)]],
      aplicaATanda: [false],
      esNoTradicional: [false],
      active: [true, Validators.required]
    });
  }

  loadTipoMaterial(): void {
    const newTipoMaterialId = this.route.snapshot.paramMap.get('id');

    if (newTipoMaterialId !== this.tipoMaterialId) {
      this.tipoMaterialForm.reset();
      this.tipoMaterial.set(null);
    }

    this.tipoMaterialId = newTipoMaterialId;

    if (this.tipoMaterialId && this.tipoMaterialId !== 'nuevo') {
      this.isEditMode.set(true);
      this.loading.set(true);

      this.tipoMaterialService.getById(this.tipoMaterialId!)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (tipoMaterial: TipoMaterial) => {
            if (tipoMaterial) {
              this.tipoMaterial.set(tipoMaterial);
              this.tipoMaterialForm.patchValue({
                codigo: tipoMaterial.codigo || '',
                nombre: tipoMaterial.nombre || '',
                factorPrecio: tipoMaterial.factorPrecio ? parseFloat(tipoMaterial.factorPrecio.toString()) : 0,
                aplicaATanda: tipoMaterial.aplicaATanda ?? false,
                esNoTradicional: tipoMaterial.esNoTradicional ?? false,
                active: tipoMaterial.active ?? true
              });
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Tipo de material no encontrado',
                life: 3000
              });
              this.router.navigate(['/tipos-material']);
            }
            this.loading.set(false);
          },
          error: (error) => {
            this.loading.set(false);
            console.error('Error al cargar tipo de material:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Error al cargar los datos del tipo de material',
              life: 3000
            });
          }
        });
    } else {
      this.isEditMode.set(false);
      // Valores por defecto para nuevo tipo de material
      this.tipoMaterialForm.patchValue({
        aplicaATanda: false,
        esNoTradicional: false,
        active: true
      });
    }
  }

  onSubmit(): void {
    if (this.tipoMaterialForm.invalid) {
      this.markFormGroupTouched(this.tipoMaterialForm);
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulario Inválido',
        detail: 'Por favor complete todos los campos requeridos',
        life: 3000
      });
      return;
    }

    const formValue = this.tipoMaterialForm.value;

    const tipoMaterial: TipoMaterial = {
      codigo: formValue.codigo,
      nombre: formValue.nombre,
      factorPrecio: formValue.factorPrecio.toString(),
      aplicaATanda: formValue.aplicaATanda,
      esNoTradicional: formValue.esNoTradicional,
      active: formValue.active
    };

    if (this.isEditMode()) {
      tipoMaterial.id = parseInt(this.tipoMaterialId!, 10);
    }

    this.loading.set(true);

    const operation = this.isEditMode()
      ? this.tipoMaterialService.update(this.tipoMaterialId!, tipoMaterial)
      : this.tipoMaterialService.create(tipoMaterial);

    operation.subscribe({
      next: (response) => {
        this.loading.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: this.isEditMode() ? 'Tipo de material actualizado correctamente' : 'Tipo de material creado correctamente',
          life: 3000
        });
        setTimeout(() => {
          this.router.navigate(['/tipos-material']);
        }, 1500);
      },
      error: (error) => {
        this.loading.set(false);
        console.error('Error al guardar tipo de material:', error);

        let errorMessage = 'Error al guardar el tipo de material';

        if (error.status === 422 && error.error?.violations) {
          const violations = error.error.violations;
          const fieldErrors: { [key: string]: string } = {};

          violations.forEach((violation: any) => {
            const field = violation.propertyPath;
            const message = violation.message;
            fieldErrors[field] = message;

            if (this.tipoMaterialForm.get(field)) {
              this.tipoMaterialForm.get(field)?.setErrors({ 'server': message });
              this.tipoMaterialForm.get(field)?.markAsTouched();
            }
          });

          if (violations.length === 1) {
            errorMessage = violations[0].message;
          } else {
            errorMessage = `Se encontraron ${violations.length} errores de validación`;
          }
        } else if (error.error?.detail) {
          errorMessage = error.error.detail;
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }

        this.messageService.add({
          severity: 'error',
          summary: 'Error de Validación',
          detail: errorMessage,
          life: 5000
        });
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/tipos-material']);
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  get pageTitle(): string {
    return this.isEditMode() ? 'Editar Tipo de Material' : 'Nuevo Tipo de Material';
  }
}
